import AuthModal, {AuthModalState, AuthModalType} from '@gaia/components/AuthModal';
import usePersistentState from '@gaia/hooks/usePersistentState';
import {UserProvider, useUserContext} from '@gaia/hooks/useUserContext';
import theme from '@gaia/theme';
import {getMobilePlatform, isMobilePlatform} from '@gaia/utils/queryNavigator';
import AuthModalContext from '@map/contexts/AuthModalContext';
import {
    UpsellModalProvider,
    type UpsellModalContext,
    type UpsellModalState,
} from '@map/contexts/UpsellModalContext';
import {Map} from '@map/Map';
import {sendViewedMainMapEvent} from '@map/Map/mapAnalyticsEvents';
import {MobileModal} from '@map/MobileModal';
import UpsellModal from '@map/popups/Upsell/UpsellModal';
import {StyledEngineProvider, ThemeProvider} from '@mui/material/styles';
import {withLDProvider} from 'launchdarkly-react-client-sdk';
import {useCallback, useEffect, useMemo, useState} from 'react';
import ReactDOM from 'react-dom';
import {GoogleReCaptchaProvider} from 'react-google-recaptcha-v3';
import Helmet from 'react-helmet';

const LaunchDarklyProvider = withLDProvider({
    clientSideID: process.env.LAUNCHDARKLY_CLIENT_SIDE_ID!,
    user: {
        anonymous: true,
        key: process.env.LAUNCHDARKLY_ANONYMOUS_USER_KEY,
    },
})(({children}) => <>{children}</>);

ReactDOM.render(<App />, document.querySelector('#root'));

function App() {
    // This analytics event is sent from this entrypoint because other entrypoints use the `<Map>`.
    // Once there is a separate `<EmbeddedMap>`, this can be moved.
    useEffect(() => sendViewedMainMapEvent(), []);

    // The entrypoint of the app contains the state required for manipulating the modals that
    // appear over the entire app. There are two such modals:
    //   - AuthModal: allows the user to login or create an account
    //   - UpsellModal: presents onboarding upsell as well as contextual paywalls to the user
    const [authModalState, setAuthModalState] = useState<AuthModalState>({
        type: undefined,
        analyticsContext: undefined,
        onUserAuthenticated: (form) => {
            if (form === 'register') {
                setUpsellModal({selectedScreen: undefined});
            }
        },
    });

    const setAuthModalType = useCallback(
        (type: AuthModalType | undefined) => setAuthModalState({...authModalState, type}),
        [setAuthModalState, authModalState]
    );

    const [upsellModal, setUpsellModal] = useState<UpsellModalState | undefined>(undefined);

    const upsellModalContext = useMemo<UpsellModalContext>(
        () => ({
            upsellModalState: upsellModal,
            setUpsellModalState: setUpsellModal,
        }),
        [upsellModal]
    );

    // The AuthModalContext provides this function instead of the raw SetState function from the setState hook.
    // contextSetAuthModal wraps the onUserAuthenticated callback to also include a trigger for displaying
    // the user onboarding upsell modal.
    const contextSetAuthModal = useCallback((state: AuthModalState) => {
        setAuthModalState({
            ...state,
            onUserAuthenticated: (form) => {
                if (form === 'register') setUpsellModal({selectedScreen: undefined});
                state.onUserAuthenticated?.();
            },
        });
    }, []);

    const [isMobileModalDismissed, setIsMobileModalDismissed] = usePersistentState(
        'mobile_modal_dismissed',
        false
    );

    return (
        <UserProvider>
            <MapHelmet />

            <LaunchDarklyProvider>
                <GoogleReCaptchaProvider reCaptchaKey={process.env.RECAPTCHA_SITE_KEY!}>
                    <AuthModalContext.Provider value={contextSetAuthModal}>
                        <UpsellModalProvider value={upsellModalContext}>
                            <StyledEngineProvider injectFirst>
                                <ThemeProvider theme={theme}>
                                    <Map
                                        savePosition
                                        showControlsDrawer
                                        showDrawControl
                                        showPreferencesControl
                                        showPrintControl
                                        showSearchBoxControl
                                        show3DControl
                                    />
                                    <AuthModal
                                        setAuthModalType={setAuthModalType}
                                        {...authModalState}
                                    />
                                    <UpsellModal />
                                </ThemeProvider>
                            </StyledEngineProvider>
                        </UpsellModalProvider>
                    </AuthModalContext.Provider>
                </GoogleReCaptchaProvider>
            </LaunchDarklyProvider>

            {isMobilePlatform() && !isMobileModalDismissed && (
                <MobileModal dismissModal={() => setIsMobileModalDismissed(true)} />
            )}

            <Analytics />

            <SUWarning />
        </UserProvider>
    );
}

function MapHelmet() {
    return (
        <Helmet>
            <script type='application/ld+json'>{`
                    {
                        "@context": "http://schema.org/",
                        "@type": "Product",
                        "@id": "https://www.gaiagps.com${location.pathname}${location.search}",
                        "name": "",
                        "description": "Access free topo maps online and use intuitive mapping tools to plan your next adventure. The Gaia GPS map catalog includes detailed satellite imagery, worldwide trail maps, weather forecasting overlays, and avalanche data overlays.",
                        "brand": {
                            "@type": "Thing",
                            "@id": "https://www.gaiagps.com/"
                        },
                        "aggregateRating": {
                            "@type": "AggregateRating",
                            "ratingValue": "4.8",
                            "reviewCount": "6662"
                        },
                        "offers": {
                            "@type": "Offer",
                            "priceCurrency": "USD",
                            "price": "39.99",
                            "priceValidUntil": "2045-12-31",
                            "seller": {
                                "@type": "Organization",
                                "name": "Gaia GPS"
                            }
                        },
                        "potentialAction": {
                            "@type": "BuyAction",
                            "seller": {
                                "@id": "https://www.gaiagps.com/"
                            },
                            "target": "https://www.gaiagps.com/register?next=${location.pathname}&show_form=true"
                        }
                    }
                `}</script>

            {getMobilePlatform() === 'iOS' ? (
                <style>{`.hide-if-ios {display: none;}`}</style>
            ) : getMobilePlatform() === 'Android' ? (
                <style>{`.hide-if-android {display: none;}`}</style>
            ) : null}
        </Helmet>
    );
}

function Analytics() {
    if (!useUserContext().webAnalyticsEnabled) return null;

    return (
        <Helmet>
            <script
                async
                src={`https://www.googletagmanager.com/gtag/js?id=${process.env.GOOGLE_ANALYTICS_KEY}`}
            />
            <script>{`
                        window.dataLayer = window.dataLayer || [];
                        function gtag(){dataLayer.push(arguments);}
                        gtag('js', new Date());

                        gtag('config', '${process.env.GOOGLE_ANALYTICS_KEY}');
                        gtag('config', '${process.env.GOOGLE_ADWORDS_KEY}');
                    `}</script>

            {DEPLOYMENT === 'production' ? (
                <script>{`
                            !function(t,e,n,s,a,c,i,o,p){t.AppsFlyerSdkObject=a,t.AF=t.AF||function(){
                            (t.AF.q=t.AF.q||[]).push([Date.now()].concat(Array.prototype.slice.call(arguments)))},
                            t.AF.id=t.AF.id||i,t.AF.plugins={},o=e.createElement(n),p=e.getElementsByTagName(n)[0],o.async=1,
                            o.src="https://websdk.appsflyer.com?"+(c.length>0?"st="+c.split(",").sort().join(",")+"&":"")+(i.length>0?"af_id="+i:""),
                            p.parentNode.insertBefore(o,p)}(window,document,"script",0,"AF","pba",{pba: {webAppId: "49c8a588-ae26-4dc7-aa50-0fc55401a408", measurementStatus: true}})
                        `}</script>
            ) : (
                <script>{`window.AF = () => {};`}</script>
            )}
        </Helmet>
    );
}

function SUWarning() {
    if (!useUserContext().isSU) return null;

    return (
        <div
            id='django-su-warning'
            style={{
                position: 'fixed',
                bottom: 0,
                width: '100%',
                backgroundColor: 'orange',
                textAlign: 'center',
                fontSize: '150%',
                zIndex: 5000,
            }}
        >
            <a href='/su' style={{color: 'white'}}>
                WARNING: You are logged in as a user! Click here to log out.
            </a>
        </div>
    );
}
